/**
 * export.ts — Step 5: Export formats
 *
 * toLUTJSON          — LUT3D → JSON string (portable, reloadable)
 * toLUTCSV           — LUT3D → CSV string  (grid node table)
 * toLUTICC           — LUT3D → Uint8Array  (ICC v4 DeviceLink profile, mft2 / lut16Type)
 * applyLUTToImageData — ImageData → corrected ImageData (browser Canvas API)
 * composeLUTWithICC  — compose DeviceLink with existing ICC AToB0 mft2 tag
 */

import type { LUT3D, MeasuredPatch } from '@/types';
import { applyLUT } from '@/engine/lut-builder';

// =============================================================================
// Shared meta
// =============================================================================

export interface LUTExportMeta {
  description?: string;
  copyright?:   string;
  /** When present, embedded as "patches" in the JSON export. */
  patches?:     MeasuredPatch[];
}

// =============================================================================
// JSON export
// =============================================================================

export interface LUTJsonPayload {
  version:       string;
  description:   string;
  gridSize:      number;
  interpolation: string;
  smoothing:     number;
  buildTimeMs:   number;
  validation:    import('@/types').LUTValidation | null;
  /** Flat array matching Float32Array layout: [R0G0B0_r, R0G0B0_g, R0G0B0_b, ...] */
  data:          number[];
  patches?:      MeasuredPatch[];
}

export function toLUTJSON(lut: LUT3D, meta: LUTExportMeta = {}): string {
  const payload: LUTJsonPayload = {
    version:       '1.0',
    description:   meta.description ?? 'RGB Calibration LUT',
    gridSize:      lut.gridSize,
    interpolation: lut.options.interpolation,
    smoothing:     lut.options.smoothing,
    buildTimeMs:   lut.buildTimeMs,
    validation:    lut.validation,
    data:          Array.from(lut.data),
  };
  if (meta.patches) payload.patches = meta.patches;
  return JSON.stringify(payload);
}

// =============================================================================
// CSV export
// =============================================================================

export function toLUTCSV(lut: LUT3D): string {
  const { data, gridSize: gs } = lut;
  const rows: string[] = ['input_R,input_G,input_B,output_R,output_G,output_B'];

  for (let ri = 0; ri < gs; ri++) {
    const denom = (gs - 1) || 1;
    const rIn = ri * 255 / denom;
    for (let gi = 0; gi < gs; gi++) {
      const gIn = gi * 255 / denom;
      for (let bi = 0; bi < gs; bi++) {
        const bIn  = bi * 255 / denom;
        const base = (ri * gs * gs + gi * gs + bi) * 3;
        const rOut = data[base] ?? 0;
        const gOut = data[base + 1] ?? 0;
        const bOut = data[base + 2] ?? 0;
        rows.push(
          round2(rIn) + ',' + round2(gIn) + ',' + round2(bIn) + ',' +
          round2(rOut) + ',' + round2(gOut) + ',' + round2(bOut),
        );
      }
    }
  }

  return rows.join('\n');
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

// =============================================================================
// ICC export — binary DeviceLink profile (v4, mft2 / lut16Type)
// =============================================================================

// ── Binary buffer writer (big-endian) ─────────────────────────────────────────

class BufWriter {
  private buf: number[] = [];

  get length(): number { return this.buf.length; }

  uint8(v: number):  void { this.buf.push(v & 0xFF); }
  uint16(v: number): void { this.uint8(v >> 8); this.uint8(v); }
  uint32(v: number): void { this.uint16((v >>> 16) & 0xFFFF); this.uint16(v & 0xFFFF); }

  /** s15Fixed16: encode real number as fixed-point int32 (big-endian). */
  s15f16(v: number): void {
    const i = Math.round(v * 65536);
    // Write as uint32 bits (two's complement preserved)
    const u = i < 0 ? (i + 0x100000000) : i;
    this.uint32(u);
  }

  /** 4-char ASCII tag signature. */
  tag4(s: string): void {
    for (let i = 0; i < 4; i++) this.uint8(s.charCodeAt(i) || 0);
  }

  zeros(n: number): void { for (let i = 0; i < n; i++) this.buf.push(0); }

  /** Pad to next 4-byte boundary. */
  align4(): void { while (this.buf.length % 4 !== 0) this.buf.push(0); }

  /** Overwrite 4 bytes at offset with a uint32. */
  patch32(offset: number, v: number): void {
    this.buf[offset]     = (v >>> 24) & 0xFF;
    this.buf[offset + 1] = (v >>> 16) & 0xFF;
    this.buf[offset + 2] = (v >>>  8) & 0xFF;
    this.buf[offset + 3] =  v         & 0xFF;
  }

  toUint8Array(): Uint8Array { return new Uint8Array(this.buf); }
}

// ── mluc tag (multiLocalizedUnicodeType, ICC v4) ──────────────────────────────

function writeMluc(w: BufWriter, text: string): void {
  const start = w.length;
  w.tag4('mluc');       // type signature
  w.zeros(4);           // reserved
  w.uint32(1);          // number of records
  w.uint32(12);         // record size (always 12)

  // Record 0: language='en', country='US'
  const strOffsetFromTagStart = 28;   // 4+4+4+4+12 = 28
  const strByteLen = text.length * 2; // UTF-16BE, 2 bytes per char
  w.uint8(0x65); w.uint8(0x6E); // 'en'
  w.uint8(0x55); w.uint8(0x53); // 'US'
  w.uint32(strByteLen);
  w.uint32(strOffsetFromTagStart);

  // UTF-16BE string
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    w.uint8((code >> 8) & 0xFF);
    w.uint8(code & 0xFF);
  }

  void start; // offset tracking not needed here
}

// ── wtpt tag (XYZType, D50 white point) ──────────────────────────────────────

function writeWtpt(w: BufWriter): void {
  w.tag4('XYZ ');    // type signature
  w.zeros(4);        // reserved
  w.s15f16(0.9642);  // X D50
  w.s15f16(1.0000);  // Y D50
  w.s15f16(0.8249);  // Z D50
  // Total: 20 bytes
}

// ── Identity 256-entry curve (uint16) ─────────────────────────────────────────

function writeIdentityCurve1(w: BufWriter): void {
  for (let i = 0; i < 256; i++) {
    // Map 0..255 → 0..65535  (× 257)
    w.uint16(Math.round(i * 65535 / 255));
  }
}

function writeIdentityCurves(w: BufWriter, channels: number): void {
  for (let c = 0; c < channels; c++) writeIdentityCurve1(w);
}

// ── mft2 (lut16Type) CLUT from LUT3D ─────────────────────────────────────────

function writeMft2(w: BufWriter, lut: LUT3D): void {
  const { data, gridSize: gs } = lut;
  const n = gs;

  w.tag4('mft2');   // type signature
  w.zeros(4);       // reserved
  w.uint8(3);       // number of input channels
  w.uint8(3);       // number of output channels
  w.uint8(n);       // grid points per axis
  w.uint8(0);       // reserved

  // 3×3 identity matrix in s15Fixed16 (row-major)
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      w.s15f16(row === col ? 1.0 : 0.0);
    }
  }

  // Input/output shaper table entry counts (mft2 spec, bytes 48-51)
  w.uint16(256);  // number of input table entries
  w.uint16(256);  // number of output table entries

  // Input curves: 3 channels × 256 entries (identity)
  writeIdentityCurves(w, 3);

  // CLUT: gs^3 nodes × 3 channels, uint16, B varies fastest
  for (let ri = 0; ri < n; ri++) {
    for (let gi = 0; gi < n; gi++) {
      for (let bi = 0; bi < n; bi++) {
        const base = (ri * n * n + gi * n + bi) * 3;
        // Scale [0,255] float → [0,65535] uint16
        w.uint16(Math.min(65535, Math.max(0, Math.round((data[base] ?? 0)     * 65535 / 255))));
        w.uint16(Math.min(65535, Math.max(0, Math.round((data[base + 1] ?? 0) * 65535 / 255))));
        w.uint16(Math.min(65535, Math.max(0, Math.round((data[base + 2] ?? 0) * 65535 / 255))));
      }
    }
  }

  // Output curves: 3 channels × 256 entries (identity)
  writeIdentityCurves(w, 3);
}

// ── Date/time 12-byte field ───────────────────────────────────────────────────

function writeDateTime(w: BufWriter): void {
  const d = new Date();
  w.uint16(d.getUTCFullYear());
  w.uint16(d.getUTCMonth() + 1);
  w.uint16(d.getUTCDate());
  w.uint16(d.getUTCHours());
  w.uint16(d.getUTCMinutes());
  w.uint16(d.getUTCSeconds());
}

// ── Main ICC builder ──────────────────────────────────────────────────────────

/**
 * Build an ICC v4 DeviceLink profile encapsulating the 3D correction LUT.
 *
 * Profile structure:
 *   128-byte header
 *   Tag count (4 bytes)
 *   Tag entries: desc, cprt, wtpt, A2B0  (4 × 12 bytes)
 *   Tag data:    mluc desc | mluc cprt | XYZ wtpt | mft2 A2B0
 */
export function toLUTICC(lut: LUT3D, meta: LUTExportMeta = {}): Uint8Array {
  const desc = meta.description ?? 'RGB Calibration LUT';
  const cprt = meta.copyright   ?? '(c) RGB Calibration';

  // ── Pre-compute tag sizes ──────────────────────────────────────────────────
  const descSize = 28 + desc.length * 2;   // mluc header + UTF-16BE string
  const descPad  = (4 - descSize % 4) % 4;
  const descTotal = descSize + descPad;

  const cprtSize = 28 + cprt.length * 2;
  const cprtPad  = (4 - cprtSize % 4) % 4;
  const cprtTotal = cprtSize + cprtPad;

  // mft2: 4+4+4+36(matrix)+4(counts)+1536(in)+CLUT+1536(out)
  const gs = lut.gridSize;
  const clutBytes = gs * gs * gs * 3 * 2;
  const mft2Size  = 4 + 4 + 4 + 36 + 4 + 1536 + clutBytes + 1536;
  // mft2 is already aligned (all fields are 2-byte aligned, total is even)
  const wtptSize  = 20;  // XYZType: 4+4+12

  // ── Offsets ────────────────────────────────────────────────────────────────
  const HEADER_END  = 128;
  const TAG_COUNT   = HEADER_END;            // 128
  const TAG_ENTRIES = TAG_COUNT + 4;         // 132
  const DATA_START  = TAG_ENTRIES + 4 * 12;  // 180 (4 tags × 12 bytes)

  const descOffset = DATA_START;
  const cprtOffset = descOffset + descTotal;
  const wtptOffset = cprtOffset + cprtTotal;
  const a2b0Offset = wtptOffset + wtptSize;

  const totalSize  = a2b0Offset + mft2Size;

  // ── Write buffer ───────────────────────────────────────────────────────────
  const w = new BufWriter();

  // ── Header (128 bytes) ────────────────────────────────────────────────────
  const sizeOffset = 0;
  w.uint32(totalSize);          // 0: profile size (filled here)
  w.tag4('lcms');               // 4: preferred CMM
  w.uint32(0x04200000);         // 8: version 4.2.0.0
  w.tag4('link');               // 12: profile class
  w.tag4('RGB ');               // 16: data colour space
  w.tag4('RGB ');               // 20: PCS (destination space for device link)
  writeDateTime(w);             // 24: date/time (12 bytes)
  w.tag4('acsp');               // 36: file signature
  w.zeros(4);                   // 40: platform (unspecified)
  w.zeros(4);                   // 44: flags
  w.zeros(4);                   // 48: device manufacturer
  w.zeros(4);                   // 52: device model
  w.zeros(8);                   // 56: device attributes
  w.uint32(0);                  // 64: rendering intent (perceptual)
  // 68: D50 illuminant XYZ in s15Fixed16
  w.s15f16(0.9642);             // X
  w.s15f16(1.0000);             // Y
  w.s15f16(0.8249);             // Z
  w.tag4('rgb!');               // 80: creator
  w.zeros(16);                  // 84: profile ID (not computed)
  w.zeros(28);                  // 100: reserved
  // Total header = 128 bytes ✓

  // ── Tag count ─────────────────────────────────────────────────────────────
  w.uint32(4);

  // ── Tag entries (4 × 12 bytes) ────────────────────────────────────────────
  // desc
  w.tag4('desc');    w.uint32(descOffset);  w.uint32(descSize);
  // cprt
  w.tag4('cprt');    w.uint32(cprtOffset);  w.uint32(cprtSize);
  // wtpt (D50 white point — required by ICC parsers and CMMs)
  w.tag4('wtpt');    w.uint32(wtptOffset);  w.uint32(wtptSize);
  // AToB0
  w.tag4('A2B0');    w.uint32(a2b0Offset);  w.uint32(mft2Size);

  // ── Tag data ──────────────────────────────────────────────────────────────
  // desc (mluc)
  writeMluc(w, desc);
  w.zeros(descPad);

  // cprt (mluc)
  writeMluc(w, cprt);
  w.zeros(cprtPad);

  // wtpt (XYZType)
  writeWtpt(w);

  // AToB0 (mft2)
  writeMft2(w, lut);

  // Sanity-check: patch size field if off (shouldn't happen, but be safe)
  const actual = w.length;
  if (actual !== totalSize) w.patch32(sizeOffset, actual);

  return w.toUint8Array();
}

// =============================================================================
// Apply LUT to browser ImageData (Canvas API)
// =============================================================================

/**
 * Apply a 3D correction LUT to every pixel in an ImageData.
 * Returns a new ImageData; the original is not modified.
 * Alpha channel is preserved unchanged.
 */
export function applyLUTToImageData(imageData: ImageData, lut: LUT3D): ImageData {
  const { data: src, width, height } = imageData;
  const out = new ImageData(width, height);
  const dst = out.data;
  for (let i = 0; i < src.length; i += 4) {
    const [ro, go, bo] = applyLUT(lut, src[i]!, src[i + 1]!, src[i + 2]!);
    dst[i]     = ro;
    dst[i + 1] = go;
    dst[i + 2] = bo;
    dst[i + 3] = src[i + 3]!;
  }
  return out;
}

// =============================================================================
// Compose DeviceLink LUT with existing ICC profile (mft2 / lut16Type AToB0)
// =============================================================================

// ── ICC binary helpers ────────────────────────────────────────────────────────

function iccU32(buf: Uint8Array, off: number): number {
  return (((buf[off]! << 24) | (buf[off + 1]! << 16) | (buf[off + 2]! << 8) | buf[off + 3]!) >>> 0);
}
function iccU16(buf: Uint8Array, off: number): number {
  return ((buf[off]! << 8) | buf[off + 1]!);
}
function iccSig4(buf: Uint8Array, off: number): string {
  return String.fromCharCode(buf[off]!, buf[off + 1]!, buf[off + 2]!, buf[off + 3]!);
}

function iccFindTag(icc: Uint8Array, sig: string): { offset: number; size: number } | null {
  if (icc.length < 132) return null;
  const count = iccU32(icc, 128);
  for (let i = 0; i < count; i++) {
    const base = 132 + i * 12;
    if (base + 12 > icc.length) break;
    if (iccSig4(icc, base) === sig) {
      return { offset: iccU32(icc, base + 4), size: iccU32(icc, base + 8) };
    }
  }
  return null;
}

// ── mft2 (lut16Type) parser ───────────────────────────────────────────────────

interface Mft2 {
  inCh:           number;
  outCh:          number;
  n:              number;   // grid points per axis
  inTableCount:   number;
  clutByteOffset: number;   // absolute offset of CLUT data in ICC buffer
  clut:           Uint16Array;
}

function parseMft2(icc: Uint8Array, tagOffset: number): Mft2 | null {
  if (iccSig4(icc, tagOffset) !== 'mft2') return null;
  const inCh  = icc[tagOffset + 8]!;
  const outCh = icc[tagOffset + 9]!;
  const n     = icc[tagOffset + 10]!;
  if (inCh < 1 || outCh < 1 || n < 2) return null;

  const inTableCount  = iccU16(icc, tagOffset + 48);
  const outTableCount = iccU16(icc, tagOffset + 50);
  void outTableCount;

  const inCurvesBytes  = inCh * inTableCount * 2;
  const clutByteOffset = tagOffset + 52 + inCurvesBytes;
  const clutLen        = Math.round(Math.pow(n, inCh)) * outCh;
  if (clutByteOffset + clutLen * 2 > icc.length) return null;

  const clut = new Uint16Array(clutLen);
  for (let i = 0; i < clutLen; i++) {
    clut[i] = iccU16(icc, clutByteOffset + i * 2);
  }
  return { inCh, outCh, n, inTableCount, clutByteOffset, clut };
}

// ── Trilinear interpolation in mft2 CLUT ─────────────────────────────────────

function mft2Lookup(m: Mft2, r: number, g: number, b: number): number[] {
  const { clut, n, outCh } = m;
  const posR = Math.max(0, Math.min(n - 1, r * (n - 1) / 255));
  const posG = Math.max(0, Math.min(n - 1, g * (n - 1) / 255));
  const posB = Math.max(0, Math.min(n - 1, b * (n - 1) / 255));
  const ri0 = Math.min(Math.floor(posR), n - 2);
  const gi0 = Math.min(Math.floor(posG), n - 2);
  const bi0 = Math.min(Math.floor(posB), n - 2);
  const ri1 = ri0 + 1, gi1 = gi0 + 1, bi1 = bi0 + 1;
  const fr = posR - ri0, fg = posG - gi0, fb = posB - bi0;

  const result: number[] = [];
  for (let c = 0; c < outCh; c++) {
    const at = (ri: number, gi: number, bi: number) =>
      clut[(ri * n * n + gi * n + bi) * outCh + c]!;
    const c00 = at(ri0, gi0, bi0) + fb * (at(ri0, gi0, bi1) - at(ri0, gi0, bi0));
    const c01 = at(ri0, gi1, bi0) + fb * (at(ri0, gi1, bi1) - at(ri0, gi1, bi0));
    const c10 = at(ri1, gi0, bi0) + fb * (at(ri1, gi0, bi1) - at(ri1, gi0, bi0));
    const c11 = at(ri1, gi1, bi0) + fb * (at(ri1, gi1, bi1) - at(ri1, gi1, bi0));
    const c0  = c00 + fg * (c01 - c00);
    const c1  = c10 + fg * (c11 - c10);
    result.push(Math.max(0, Math.min(65535, Math.round(c0 + fr * (c1 - c0)))));
  }
  return result;
}

// ── Public: compose DeviceLink with ICC AToB0 ─────────────────────────────────

export type ComposeLUTResult =
  | { ok: true;  data: Uint8Array }
  | { ok: false; error: string };

/**
 * Compose a DeviceLink LUT with an existing ICC output profile's AToB0 tag.
 *
 * For each grid node (R, G, B) in the ICC's AToB0 mft2 CLUT:
 *   1. Apply the DeviceLink correction: (R', G', B') = DeviceLink(R, G, B)
 *   2. Interpolate the original CLUT at (R', G', B') to get the PCS value
 *   3. Store that PCS value in the new node at (R, G, B)
 *
 * Result: an ICC profile that maps uncorrected device RGB → PCS directly,
 * with the DeviceLink correction baked in.
 *
 * Only mft2 (lut16Type) AToB0 tags are supported.
 */
export function composeLUTWithICC(iccBytes: Uint8Array, lut: LUT3D): ComposeLUTResult {
  if (iccBytes.length < 132) return { ok: false, error: 'File is too small to be an ICC profile' };

  const tag = iccFindTag(iccBytes, 'A2B0');
  if (!tag) return { ok: false, error: 'No AToB0 tag found — unsupported profile type (needs output or DeviceLink profile)' };

  const typeSig = iccSig4(iccBytes, tag.offset);
  if (typeSig !== 'mft2') {
    return { ok: false, error: `AToB0 uses type '${typeSig.trim()}' — only mft2 (lut16Type) is supported` };
  }

  const m = parseMft2(iccBytes, tag.offset);
  if (!m) return { ok: false, error: 'Failed to parse AToB0 mft2 tag' };
  if (m.inCh !== 3) return { ok: false, error: `Profile has ${m.inCh} input channels (3 required)` };

  // Build composed CLUT: new_node(R,G,B) = old_CLUT(DeviceLink(R,G,B))
  const { n, outCh } = m;
  const newClut = new Uint16Array(m.clut.length);

  for (let ri = 0; ri < n; ri++) {
    for (let gi = 0; gi < n; gi++) {
      for (let bi = 0; bi < n; bi++) {
        const r = ri * 255 / (n - 1);
        const g = gi * 255 / (n - 1);
        const b = bi * 255 / (n - 1);
        const [rc, gc, bc] = applyLUT(lut, r, g, b);
        const vals = mft2Lookup(m, rc, gc, bc);
        const base = (ri * n * n + gi * n + bi) * outCh;
        for (let c = 0; c < outCh; c++) newClut[base + c] = vals[c]!;
      }
    }
  }

  // Copy original ICC bytes and overwrite CLUT in-place
  const output = new Uint8Array(iccBytes);
  for (let i = 0; i < newClut.length; i++) {
    const v = newClut[i]!;
    output[m.clutByteOffset + i * 2]     = (v >> 8) & 0xFF;
    output[m.clutByteOffset + i * 2 + 1] =  v       & 0xFF;
  }
  return { ok: true, data: output };
}
